import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import { AutoCompletedFields } from '../../../../../types/formWithAutoCompletedFields'
import { isUndefinedOrEmptyObject } from '../../../../../utils/isUndefinedOrEmptyObject'
import {
  BillForm,
  BillFormBasic,
  BillFormLineKeys,
  BillFormPartial,
  getDefaultBillLine,
  getDefaultValues,
} from './formData'
import { getShouldOverridenParamChange } from './getShouldOverridenParamChange'

interface ShouldOverrvideValueProps {
  object: Record<string, any>
  property: any
  valueCurrent: any
  valueOverriden: any
  defaultValue: any
}

const shouldOverrideValue = ({
  object,
  property,
  valueCurrent,
  valueOverriden,
  defaultValue,
}: ShouldOverrvideValueProps) =>
  Object.prototype.hasOwnProperty.call(object, property) &&
  !isUndefinedOrEmptyObject(valueOverriden) &&
  !isEqual(valueOverriden, defaultValue) &&
  (isUndefinedOrEmptyObject(valueCurrent) || isEqual(valueCurrent, defaultValue))

export interface AutoCompleteBillFormProps {
  billFormDefault: BillForm
  billFormOverrides: BillFormPartial
  baseCurrencyId?: string
  onlyFirstBillLine?: boolean
  tagChanged?: boolean
}

export const autoCompleteBillForm = ({
  billFormDefault,
  billFormOverrides,
  baseCurrencyId,
  onlyFirstBillLine,
}: AutoCompleteBillFormProps): BillForm => {
  const billForm: BillForm = cloneDeep(billFormDefault)
  const autoCompletedFields: AutoCompletedFields<BillFormBasic> = {}
  const billDefaultValues = getDefaultValues(baseCurrencyId)
  const billDefaultBillLine = getDefaultBillLine()

  billForm.billLines = []

  for (let i = 0; i < (billFormOverrides.billLines?.length ?? billFormDefault.billLines?.length ?? 0); i++) {
    billForm.billLines?.push(cloneDeep(billFormDefault.billLines?.[i] || getDefaultBillLine()))
  }

  for (const property in billForm) {
    if (property === 'billLines') {
      continue
    }

    const valueCurrent = billForm[property]
    const valueOverriden = billFormOverrides[property]
    const defaultValue = billDefaultValues[property]
    const hasBeenAlreadyAutoCompleted = billForm?.autoCompletedFields?.[property]

    const shouldOverride = shouldOverrideValue({
      object: billDefaultValues,
      property,
      valueCurrent,
      valueOverriden,
      defaultValue,
    })

    if (shouldOverride || (hasBeenAlreadyAutoCompleted && valueOverriden !== undefined)) {
      billForm[property] = valueOverriden
      autoCompletedFields[property] = true
    }
  }

  if (billForm.billLines?.length && billFormOverrides.billLines?.length) {
    for (let i = 0; i < billForm.billLines.length; i++) {
      const isFirstLine = i === 0

      if (onlyFirstBillLine && !isFirstLine) {
        break
      }

      for (const property in billForm.billLines[i]) {
        const valueCurrent = billForm.billLines[i][property]
        const valueOverriden = (billFormOverrides.billLines[i] || billFormOverrides.billLines[0])?.[property]
        const defaultValue = billDefaultBillLine[property]
        const autoCompletedProperty = `billLines.${i}.${property}`
        const hasBeenAlreadyAutoCompleted = billForm?.autoCompletedFields?.[autoCompletedProperty]
        /* Vendor based properties are only locked when they are present */
        /* Empty values can be overwritten */
        const propertiesLocked = billForm.billLines[i].lockedByVendor && !isEmpty(valueCurrent)

        const shouldOverwriteValue =
          hasBeenAlreadyAutoCompleted ||
          shouldOverrideValue({
            object: billDefaultBillLine,
            property,
            valueCurrent,
            valueOverriden,
            defaultValue,
          })

        const shouldParamChange =
          shouldOverwriteValue &&
          getShouldOverridenParamChange({
            property: property as BillFormLineKeys,
            propertiesLocked,
            hasValueOverriden: !!valueOverriden,
            vendor: billForm.vendor || billFormOverrides.vendor,
          })

        if (shouldParamChange) {
          billForm.billLines[i][property] = valueOverriden
          autoCompletedFields[autoCompletedProperty] = !!valueOverriden
        }
      }
    }
  }

  if (!isEmpty(autoCompletedFields)) {
    billForm.autoCompletedFields = { ...(billForm.autoCompletedFields || {}), ...autoCompletedFields }
  }

  return billForm
}
